import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import { environment } from '../../environments/environment';
import { AppStateService } from '../app-state.service';
import { getApiHeaders } from '../utils/api-headers.util';
import { transformClinicInfo } from '../utils/api-transformers.util';

@Injectable({
  providedIn: 'root'
})
export class FormConfigService {
  private baseUrl: string = environment.apiUrl;
  orgTenantId: string | null

  constructor(
    private http: HttpClient,
    private appStateService: AppStateService,
    private authService: AuthService,
  ) {
     this.orgTenantId = this.appStateService.getState().orgTenantId;
     // Use tenant subdomain in all environments for proper multi-tenancy
     if (this.orgTenantId) {
      this.baseUrl = this.baseUrl.replace('https://', `https://${this.orgTenantId}.`);
     }
    }

  private getHeaders() {
    const authToken = this.authService.getAccessToken();
    return getApiHeaders(authToken);
  }

  // DEPRECATED: Use AppointmentService.fetchAppointments instead
  fetchAppointments(client_id: string, patient_id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${client_id}/patients/${patient_id}/appointments`, {
      headers: this.getHeaders()
    });
  }

  // DEPRECATED: Use AppointmentService.manageAppointment instead
  manageAppointment(clientId: string, patientId: string, appointmentNumber: string, action: string): Observable<any> {
    const payload = {
      appointmentNumber,
      action: action,
      clientId: clientId,
      patientId: patientId
    };
    return this.http.post<any>(`${this.baseUrl}/appointment/manage`, payload, {
      headers: this.getHeaders()
    });
  }

  postReview(client_id: string, patient_id: string, review_data: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/${client_id}/patients/${patient_id}/reviews`, review_data, {
      headers: this.getHeaders()
    });
  }

  getClientConfig(client_id: string, config_name: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${client_id}/client_config/${config_name}`, {
      headers: this.getHeaders()
    });
  }


  lookupPatient(client_id: string, patient: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/${client_id}/patients/register`, patient, {
      headers: this.getHeaders()
    });
  }

    // family lookup
    fetchPatientFamilyLookup(client_id: string, patient: any): Observable<any> {
      return this.http.post<any>(`${this.baseUrl}/${client_id}/patients/family_lookup`, patient, {
        headers: this.getHeaders()
      });
    }

    // Kiosk/anonymous version of family lookup - let interceptor handle anonymous token
    fetchPatientFamilyLookupAnonymous(client_id: string, patient: any): Observable<any> {
      // Don't set headers - let AnonymousAuthInterceptor handle Bearer token for this endpoint
      return this.http.post<any>(`${this.baseUrl}/${client_id}/patients/family_lookup`, patient);
    }
  


    saveOnboardForm(client_id: string, patient_id: string, form_data: any): Observable<any> {
      return this.http.post<any>(`${this.baseUrl}/${client_id}/patients/${patient_id}/forms/${form_data.form_id}/save`, form_data, {
        headers: this.getHeaders()
      });
    }


  submitForm(client_id: string, patient_id: string, form_id: string, submit_info: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/${client_id}/patients/${patient_id}/forms/${form_id}/submit`, submit_info, {
      headers: this.getHeaders()
    });
  }

  fetchPatient(client_id: string, patientId: string): Observable<any> {
    const url = `${this.baseUrl}/${client_id}/patients/profile/${patientId}`;
    
    return this.http.get<any>(url,
      {
        headers: this.getHeaders(),
      });
    }

  createPatient(clientId: string, patient_data: any): Observable<any> {
      // Don't set x-api-key - let AnonymousAuthInterceptor handle Bearer token
      const headers = new HttpHeaders({
        'Content-Type': 'application/json'
      });

      return this.http.post<any>(`${this.baseUrl}/${clientId}/patients/create`, patient_data, {
        headers: headers
      });
    }

  // Kiosk/anonymous version of create patient - let interceptor handle anonymous token
  createPatientAnonymous(clientId: string, patient_data: any): Observable<any> {
    // Don't set headers - let AnonymousAuthInterceptor handle Bearer token for this endpoint
    return this.http.post<any>(`${this.baseUrl}/${clientId}/patients/create`, patient_data);
  }

  updatePatientData(client_id: string, patient_id: string, patient_data: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/${client_id}/patients/save/${patient_id}`, patient_data, {
      headers: this.getHeaders()
    });
  }

  saveConsentForm(client_id: string, patient_id: string, page_data: any): Observable<any> {
    const payload = {
      section_id: page_data.page_title,
      section_data: page_data.page_data
    };
    return this.http.post<any>(`${this.baseUrl}/${client_id}/patients/${patient_id}/consent_forms`, payload, {
      headers: this.getHeaders()
    });
  }

  getFormTemplates(client_id: string, template_id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${client_id}/forms/templates/${template_id}`, {
      headers: this.getHeaders()
    });
  }

  getAllFormTemplates(client_id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${client_id}/forms/templates/all`, {
      headers: this.getHeaders()
    });
  }

  // Kiosk/anonymous form methods - let AnonymousAuthInterceptor handle Bearer token
  // Don't set headers here - interceptor will add anonymous Bearer token

  getAllFormTemplatesAnonymous(client_id: string): Observable<any> {
    // Let interceptor handle Bearer token for anonymous access
    return this.http.get<any>(`${this.baseUrl}/${client_id}/forms/templates/all`);
  }

  getFormTemplatesAnonymous(client_id: string, template_id: string): Observable<any> {
    // Let interceptor handle Bearer token for anonymous access
    return this.http.get<any>(`${this.baseUrl}/${client_id}/forms/templates/${template_id}`);
  }

  createFormInstanceAnonymous(client_id: string, patient_id: string, template_id: string): Observable<any> {
    // Let interceptor handle Bearer token for anonymous access
    return this.http.post<any>(`${this.baseUrl}/${client_id}/patients/${patient_id}/forms/create/${template_id}`, {});
  }

  saveOnboardFormAnonymous(client_id: string, patient_id: string, form_data: any): Observable<any> {
    // Let interceptor handle Bearer token for anonymous access
    return this.http.post<any>(`${this.baseUrl}/${client_id}/patients/${patient_id}/forms/${form_data.form_id}/save`, form_data);
  }

  submitFormAnonymous(client_id: string, patient_id: string, form_id: string, submit_info: any): Observable<any> {
    // Let interceptor handle Bearer token for anonymous access
    return this.http.post<any>(`${this.baseUrl}/${client_id}/patients/${patient_id}/forms/${form_id}/submit`, submit_info);
  }

  getPatientRegistrationDataAnonymous(client_id: string, patient_id: string, formId: string): Observable<any> {
    // Let interceptor handle Bearer token for anonymous access
    return this.http.get<any>(`${this.baseUrl}/${client_id}/patients/${patient_id}/forms/${formId}`);
  }

  // Get all forms for a specific patient - kiosk mode
  getAllPatientFormsAnonymous(client_id: string, patient_id: string): Observable<any> {
    // Let interceptor handle Bearer token for anonymous access
    return this.http.get<any>(`${this.baseUrl}/${client_id}/patients/${patient_id}/forms/all`);
  }

  getAllPatientForms(client_id: string, patient_id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${client_id}/patients/${patient_id}/forms/all`, {
      headers: this.getHeaders()
    });
  }

  createFormInstance(client_id: string, patient_id: string, template_id: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/${client_id}/patients/${patient_id}/forms/create/${template_id}`, {}, {
      headers: this.getHeaders()
    });
  }

  updateFormTemplates(client_id: string, form_id: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(`${this.baseUrl}/${client_id}/forms/templates/update/${form_id}`, formData, {
      headers: new HttpHeaders({
        'Authorization': `Bearer ${this.authService.getAccessToken()}`
      })
    });
  }

  getPatientRegistrationData(client_id: string, patient_id: string, formId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${client_id}/patients/${patient_id}/forms/${formId}`, {
      headers: this.getHeaders()
    });
  }

  fetchDataSource(_endpoint: string, _method: string): Observable<any> {
    // for now load from assets/json file
    return this.http.get<any>(`assets/activity_log.json`);
  }

  getScreenConfig(): Observable<any> {
    return this.http.get('/assets/profile.json');
  }

  fetchFormPdf(client_id: string, patient_id: string, form_id: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${client_id}/patients/${patient_id}/forms/${form_id}/pdf`, {
      headers: this.getHeaders(),
      responseType: 'blob'
    });
  }

  fetchTreatmentPlans(client_id: string, patient_id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${client_id}/patients/${patient_id}/treatment-plans`, {
      headers: this.getHeaders()
    });
  }

  fetchProcedureHistory(client_id: string, patient_id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${client_id}/patients/${patient_id}/procedure-history`, {
      headers: this.getHeaders()
    });
  }

  fetchPrescriptions(client_id: string, patient_id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${client_id}/patients/${patient_id}/medications`, {
      headers: this.getHeaders()
    });
  }

  fetchNotifications(client_id: string, patient_id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${client_id}/patients/${patient_id}/communication-logs`, {
      headers: this.getHeaders()
    });
  }

  fetchInsuranceBenefits(client_id: string, patient_id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${client_id}/patients/${patient_id}/insurance-benefits`, {
      headers: this.getHeaders()
    });
  }

  uploadDocument(clientId: string, patientId: string, payload: FormData): Observable<any> {
    const url = `${this.baseUrl}/${clientId}/patients/${patientId}/upload-documents`;

    // Do NOT set 'Content-Type'; browser handles it automatically
    const headers = this.getHeaders().delete('Content-Type');

    return this.http.post<any>(url, payload, { headers });
}


  downloadDocument(clientId: string, patientId: string, documentId: string): Observable<Blob> {
    const url = `${this.baseUrl}/${clientId}/patients/${patientId}/download-documents/${documentId}`;
    return this.http.get(url, { headers: this.getHeaders(), responseType: 'blob' });
  }

  fetchDocuments(clientId: string, patientId: string): Observable<any> {
    const url = `${this.baseUrl}/${clientId}/patients/${patientId}/documents`;
    return this.http.get<any>(url, { headers: this.getHeaders() });
  }

  fetchClientInfo(client_id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${client_id}/clinic_info`, {
      headers: this.getHeaders()
    }).pipe(
      map(response => transformClinicInfo(response))
    );
  }

  fetchPublicClinicInfo(org_id: string, client_id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/public/${org_id}/${client_id}/clinic_info`, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'x-api-key': environment.apiKey
      })
    }).pipe(
      map(response => transformClinicInfo(response))
    );
  }

  saveAppointment(appointment_data: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/patients/appointment/manage`, appointment_data, {
      headers: this.getHeaders()
    });
  }

  getAvailableSlots(appointment_request: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/patients/appointment/slots`, appointment_request, {
      headers: this.getHeaders()
    });
  }

  fetchAppointmentInfo(appointment_id: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/patients/appointment/${appointment_id}`, {
      headers: this.getHeaders()
    });
  }

  fetchProcedureCodes(client_id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${client_id}/procedure-codes`, {
      headers: this.getHeaders()
    });
  }

  fetchInsuranceProviders(client_id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${client_id}/insurance/providers`, {
      headers: this.getHeaders()
    });
  }

  submitInsuranceForm(clientId: string, patientId: string, insuranceData: any): Observable<any> {
    const url = `${this.baseUrl}/${clientId}/patients/${patientId}/insurance/submit`;
    return this.http.post<any>(url, insuranceData, {
      headers: this.getHeaders()
    });
  }

}

